import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal, Icon, useToast } from '@wehunt/ui'
import { useDeleteHuntPlan } from './useDeleteHuntPlan'

/**
 * A hunt leader's Delete on a plan that has not started, rendered into
 * `HuntDetailPage`'s `planAction` slot, which the page shows on an upcoming
 * plan only. The route renders it for the plan's leaders only, the rule
 * `deletehuntplan` enforces.
 */
export function HuntPlanDeleteAction({
  groupId,
  huntPlanId,
  huntName,
  onDeleted,
}: {
  groupId: string
  huntPlanId: string
  huntName: string
  onDeleted: () => void
}) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const deletePlan = useDeleteHuntPlan()
  const [confirming, setConfirming] = useState(false)
  // A cold callable plus the list refetches can take seconds, and the leader
  // may close or leave the page meanwhile; leaving through onDeleted then would
  // pull them back to where they had come from.
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const confirmDelete = async () => {
    setConfirming(false)
    try {
      await deletePlan.mutateAsync({ groupId, huntPlanId })
    } catch {
      showToast(t('routes.huntDelete.error'))
      return
    }
    showToast(t('routes.huntDelete.deleted'))
    if (mounted.current) onDeleted()
  }

  return (
    <>
      <button
        className="wh-btn wh-btn-danger"
        disabled={deletePlan.isPending}
        onClick={() => setConfirming(true)}
      >
        <Icon name="trash" size={16} />
        {t('routes.huntDelete.action')}
      </button>
      {confirming && (
        <ConfirmModal
          title={t('routes.huntDelete.confirmTitle', { name: huntName })}
          description={t('routes.huntDelete.confirmBody')}
          confirmLabel={t('routes.huntDelete.action')}
          cancelLabel={t('actions.cancel')}
          tone="danger"
          onConfirm={() => void confirmDelete()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  )
}
